// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Configuracoes/ConfiguracaoRenave.tsx
//  RENAVE (Serpro) — configuração da EMPRESA do login + teste de conexão.
//
//  O certificado A1 (mTLS) é o mesmo da emissão fiscal: fica no serviço
//  fiscal, não aqui. A revenda precisa contratar o RENAVE na loja.serpro e
//  cadastrar o certificado no Credencia Denatran (decisões §7 do estudo).
// ════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Car, CheckCircle2, Info, Loader2, PlugZap, Save, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { getApiErrorMessage } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';
import { renaveApi, type ConfigRenave } from '@/features/renave/api';

export default function ConfiguracaoRenave() {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [config, setConfig] = useState<ConfigRenave | null>(null);
  const [form, setForm] = useState({ ativo: false, ambiente: '2', email: '', produtoModeloId: 0 });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const c = await renaveApi.config();
      setConfig(c);
      setForm({
        ativo: c.ativo,
        ambiente: String(c.ambiente),
        email: c.emailEstabelecimento ?? '',
        produtoModeloId: c.produtoModeloId ?? 0,
      });
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await renaveApi.salvarConfig({
        ativo: form.ativo,
        ambiente: Number(form.ambiente),
        emailEstabelecimento: form.email.trim() || null,
        produtoModeloId: form.produtoModeloId || null,
      });
      toast.success('Configuração do RENAVE salva.');
      await carregar();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setSalvando(false);
    }
  };

  const testar = async () => {
    setTestando(true);
    try {
      const r = await renaveApi.testarConexao();
      if (r.ok) toast.success(r.mensagem); else toast.warning(r.mensagem);
      await carregar();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setTestando(false);
    }
  };

  if (carregando) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando…</div>;
  }

  const producao = form.ambiente === '1';

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Car className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">RENAVE</h1>
          <p className="text-sm text-muted-foreground">
            Registro Nacional de Veículos em Estoque (Serpro): entrada e saída dos veículos da revenda.
          </p>
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Antes de ativar</AlertTitle>
        <AlertDescription className="space-y-1 text-sm">
          <p>1. Contrate o RENAVE na loja do Serpro (a contratação é da revenda).</p>
          <p>2. Cadastre o certificado <strong>e-CNPJ A1</strong> da empresa no <strong>Credencia Denatran</strong>.
            É o mesmo certificado usado na emissão de NF-e (certificado A3/token não funciona).</p>
          <p>3. A empresa precisa estar como <strong>Lucro Presumido ou Lucro Real</strong> (regime normal de ICMS).</p>
          <p>4. Cadastre um <strong>produto-modelo</strong> de veículo usado com CST (não CSOSN), CFOP e o ICMS por UF com a redução de base do usado, conferidos pelo contador.
            Cada veículo comprado vira um produto copiado dele.</p>
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuração da empresa</CardTitle>
          <CardDescription>Vale para o CNPJ em que você está logado.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {config && (
            <p className={`text-sm ${['Lucro Presumido', 'Lucro Real'].includes(config.regimeEmpresa) ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'}`}>
              Regime tributário da empresa: <strong>{config.regimeEmpresa}</strong>
              {!['Lucro Presumido', 'Lucro Real'].includes(config.regimeEmpresa) &&
                ' — o RENAVE no Udisys atende revendas de Lucro Presumido ou Lucro Real. Ajuste em Configurações → Empresa.'}
            </p>
          )}
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="renave-ativo" className="font-medium">RENAVE ativo</Label>
              <p className="text-xs text-muted-foreground">Exige e-mail do estabelecimento e produto-modelo.</p>
            </div>
            <Switch id="renave-ativo" checked={form.ativo} onCheckedChange={v => setForm(f => ({ ...f, ativo: v }))} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Ambiente</Label>
              <Select value={form.ambiente} onValueChange={v => setForm(f => ({ ...f, ambiente: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">Homologação (testes)</SelectItem>
                  <SelectItem value="1">Produção</SelectItem>
                </SelectContent>
              </Select>
              {producao && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Em produção as operações têm efeito legal sobre os veículos.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="renave-email">E-mail do estabelecimento *</Label>
              <Input id="renave-email" type="email" value={form.email} maxLength={120}
                placeholder="estoque@suarevenda.com.br"
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <p className="text-xs text-muted-foreground">Sempre o mesmo: vai em toda entrada e saída.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Produto-modelo de veículo *</Label>
            <ProdutoSearchCombobox
              value={form.produtoModeloId || undefined}
              onChange={id => setForm(f => ({ ...f, produtoModeloId: id }))}
              placeholder="Buscar produto-modelo…"
            />
            {config?.produtoModeloDescricao && form.produtoModeloId === config.produtoModeloId && (
              <p className="text-xs text-muted-foreground">Atual: {config.produtoModeloDescricao} (#{config.produtoModeloId})</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={salvar} disabled={salvando}>
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conexão com o RENAVE</CardTitle>
          <CardDescription>Consulta quem o Serpro reconhece pelo certificado da empresa. Não altera nada.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {config?.dtUltimoTeste ? (
            <div className="flex items-start gap-3 rounded-md border p-3">
              {config.ultimoTesteOk
                ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                : <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />}
              <div className="space-y-1 text-sm">
                <p>{config.ultimoTesteMensagem}</p>
                <p className="text-xs text-muted-foreground">
                  Testado em {new Date(config.dtUltimoTeste).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum teste feito ainda.</p>
          )}
          <Button variant="outline" onClick={testar} disabled={testando || !config?.ativo}>
            {testando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
            Testar conexão
          </Button>
          {!config?.ativo && <p className="text-xs text-muted-foreground">Salve a configuração ativa para testar.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
